// razorpay.js — shared Checkout.js loader + modal opener, used by both
// Cart.js (paying at checkout) and OrderDetail.js (retrying a pending
// payment later). The widget itself has to come from Razorpay's own CDN —
// there's no npm package for the client-side modal.
import { PORTAL } from './portalAuth';

let scriptPromise = null;
export function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve(true);
  if (!scriptPromise) {
    scriptPromise = new Promise(resolve => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * Opens the Razorpay Checkout.js modal for a given order and verifies the
 * result server-side. Never marks anything paid on the client's say-so —
 * `onVerified` only fires after POST /order/:id/razorpay/verify succeeds.
 *
 * @param {number} orderId  our own order id
 * @param {object} rp       { order_id, key_id, amount, currency, name, description, prefill } from the backend
 * @param {object} cb       { onVerified(paymentStatus), onPending(reason) } — reason is
 *                          'dismissed' | 'payment_failed' | 'verify_error' | 'script_load_failed'
 */
export async function openRazorpayCheckout(orderId, rp, { onVerified, onPending }) {
  const loaded = await loadRazorpayScript();
  if (!loaded || !window.Razorpay) { onPending('script_load_failed'); return; }

  const accentColor = getComputedStyle(document.documentElement).getPropertyValue('--sf-accent').trim() || '#0d6efd';
  const rzp = new window.Razorpay({
    key: rp.key_id, amount: rp.amount, currency: rp.currency,
    name: rp.name, description: rp.description, order_id: rp.order_id,
    prefill: rp.prefill, theme: { color: accentColor },
    handler: async (response) => {
      try {
        const v = await PORTAL.post(`/order/${orderId}/razorpay/verify`, {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        onVerified(v.data.payment_status);
      } catch (e) { onPending('verify_error'); }
    },
    modal: { ondismiss: () => onPending('dismissed') },
  });
  rzp.on('payment.failed', () => onPending('payment_failed'));
  rzp.open();
}
